import { useTopPostsControls } from '../../Posts/Instagram/useTopPostsControls.hook';
import { Button } from '@/ui/design-system/components';
import CategoryDescription from 'components/CategoryDescription';
import { FiExternalLink } from 'react-icons/fi';
import { LinkTracked } from 'components/Link';
import { getPathFromNode } from 'utils/helpers';
import DimensionIcon from '../../DimensionIcon';
import DrawerHeader from '../../DrawerHeader';
import DrawerSectionHeading from '../../DrawerSectionHeading';
import RelatedTermsDrawerSection from 'components/DataVis/RelatedCategoriesTable/RelatedTermsDrawerSection';
import { DATA_SOURCES } from '@/config/constants';
import InstagramSortableTopPosts from '../../Posts/Instagram/InstagramSortableTopPosts';
import Stack from '../../Stack';
import ToggleWatch from '../../Button/ToggleWatch';
import TopPostsDownloadButton from '../../Posts/TopPostsDownloadButton';
import { useInstagramRelatedDrawerTopPosts } from 'utils/hooks/api';
import { v5 as uuidv5 } from 'uuid';

function InstagramRelatedAggregateDrawerContent({
    entity, // 'category' | 'brand'
    week,
    year,
    countries,
    categories,
    brands,
    relatedCategories,
    relatedBrands,
    name,
    parentName,
    selectedRow,
    displayQty = 9,
    showAddToWatchlist = true,
}) {
    const {
        sortSelect,
        value,
        defaultSortOption,
        visibleOrderedMetrics,
        columnsMenu,
    } = useTopPostsControls({
        week,
        year,
        mixpanelContext: 'InstagramTopPostsSortSelect',
        tableName: 'instagram-related-aggregate-drawer',
    });

    const topPostsQuery = useInstagramRelatedDrawerTopPosts({
        entity,
        year: value.year,
        week: value.week,
        countries,
        categories,
        brands,
        relatedCategories,
        relatedBrands,
        rank_type: value.apiRankType,
        per_page: 25,
    });

    // Unified `related-category` watchlist write (matches the TT drawer flow).
    const parentEntityId =
        (categories && categories[0]) || (brands && brands[0]) || null;
    const relatedEntityId =
        entity === 'brand'
            ? selectedRow?.relatedBrandId
            : selectedRow?.relatedCategoryId;
    const showWatchlistAdd =
        showAddToWatchlist && !!(parentEntityId && relatedEntityId);
    const relatedItemTo =
        relatedEntityId &&
        getPathFromNode(
            {
                id: relatedEntityId,
                type: entity,
                depth: entity === 'category' ? 'cat5' : undefined,
            },
            null,
            'instagram',
        );
    const watchlistData = showWatchlistAdd
        ? {
              name: selectedRow?.name,
              type: 'related-category',
              relatedCategoryId: relatedEntityId,
              ...(parentName && { parentName }),
              ...(brands
                  ? { brandId: parentEntityId }
                  : { categoryId: parentEntityId }),
          }
        : null;

    return (
        <div className="flex flex-col gap-6 pb-9">
            <DrawerHeader
                name={selectedRow?.name}
                eyebrow={
                    <>
                        <DimensionIcon dimensionType={entity} />
                        {entity}
                    </>
                }
                contextLabel={parentName}
                contextPrefix="in"
            >
                {showWatchlistAdd ? (
                    <ToggleWatch
                        id={uuidv5(parentEntityId, relatedEntityId)}
                        data={watchlistData}
                        btnChildren="Add to watchlist"
                    />
                ) : null}
                {relatedItemTo ? (
                    <Button variant="default" size="sm" asChild>
                        <LinkTracked
                            to={relatedItemTo}
                            mixpanelContext="DataTableDrawer"
                            mixpanelMeta={{
                                itemNames: [selectedRow?.name, parentName],
                            }}
                        >
                            See more data
                            <FiExternalLink />
                        </LinkTracked>
                    </Button>
                ) : null}
            </DrawerHeader>
            <div className="px-4 flex flex-col gap-4">
                {entity === 'category' && relatedEntityId ? (
                    <CategoryDescription
                        categoryId={relatedEntityId}
                        categoryName={selectedRow?.name}
                    />
                ) : null}
                <RelatedTermsDrawerSection
                    source={DATA_SOURCES.INSTAGRAM}
                    entity={entity}
                    week={week}
                    year={year}
                    countries={countries}
                    categories={categories}
                    brands={brands}
                    relatedCategories={relatedCategories}
                    relatedBrands={relatedBrands}
                    name={name}
                    relatedEntityName={selectedRow?.name}
                    filename={`${name} keywords and hashtags`}
                    showIcon={false}
                />
                <Stack $space="1rem">
                    <Stack
                        $direction="row"
                        $space="15px"
                        css="display: flex; align-items: center;"
                    >
                        <DrawerSectionHeading>Top Posts</DrawerSectionHeading>
                        <span css="margin-left: auto;">
                            <TopPostsDownloadButton
                                year={value.year}
                                week={value.week}
                                name={name}
                                source="instagram"
                                topPostsQuery={topPostsQuery}
                                visibleOrderedMetrics={visibleOrderedMetrics}
                            />
                        </span>
                    </Stack>
                    <Stack
                        css="display: flex; justify-content: space-between;"
                        $space=".25rem"
                        $direction="row"
                    >
                        {sortSelect}
                        {columnsMenu}
                    </Stack>
                    <InstagramSortableTopPosts
                        topPostsQuery={topPostsQuery}
                        displayQty={displayQty}
                        sortOption={defaultSortOption}
                        visibleOrderedMetrics={visibleOrderedMetrics}
                    />
                </Stack>
            </div>
        </div>
    );
}

export default InstagramRelatedAggregateDrawerContent;
